import { useState, useEffect, useCallback, useRef } from "react";

// ─── API Config ───
const API_BASE = "https://trueloveislocked.com/api/index.php";

const api = {
  token: null,
  async call(action, method = "GET", body = null) {
    const headers = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    const opts = { method, headers };
    if (body && method === "POST") opts.body = JSON.stringify(body);
    // action might contain extra query params like "ratings?limit=7"
    const sep = action.includes("?") ? "&" : "";
    const [base, ...rest] = action.split("?");
    const qs = rest.length ? `&${rest.join("?")}` : "";
    const res = await fetch(`${API_BASE}?action=${base}${qs}`, opts);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Request failed");
    return data;
  },
  setToken(t) { this.token = t; if (t) localStorage.setItem("locked_token", t); else localStorage.removeItem("locked_token"); },
  loadToken() { this.token = localStorage.getItem("locked_token"); return !!this.token; },
};

// ─── Browser Notifications ───
// ─── Browser Notifications ───
async function requestNotificationPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await api.call("push_subscribe", "POST", { subscription: existing.toJSON() });
      return;
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array("BKFriyYDPs5IL023SupfAQsliHIP9t5KgPxKLbhaOz9RmdV9b3A3mGdaf7CK69SexT7olR2NI6EU26BlqLwrDto"),
    });
    await api.call("push_subscribe", "POST", { subscription: sub.toJSON() });
  } catch (e) { console.warn("Push subscribe failed:", e); }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

async function showNotification(title, body) {
  if (Notification.permission !== "granted") return;
  if (navigator.serviceWorker?.controller) {
    const reg = await navigator.serviceWorker.ready;
    reg.showNotification(title, { body, icon: "/icon-192.png" });
  } else {
    new Notification(title, { body, icon: "/icon-192.png" });
  }
}

// ─── Styles ───
const STYLE_TAG = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap');
:root {
  --bg-deep: #0a0a0f; --bg-card: #13131a; --bg-elevated: #1a1a24;
  --border-subtle: rgba(255,255,255,0.06); --border-accent: rgba(200,160,255,0.15);
  --text-primary: #e8e4ef; --text-secondary: #8a8494; --text-muted: #5a5464;
  --accent-kh: #c4a0ff; --accent-kh-dim: rgba(196,160,255,0.12);
  --accent-sub: #80b4ff; --accent-sub-dim: rgba(128,180,255,0.12);
  --accent-warn: #ff8a80; --accent-good: #80e8a0; --accent-gold: #ffd480;
  --radius: 16px; --radius-sm: 10px;
  --font-display: 'Playfair Display', Georgia, serif;
  --font-body: 'DM Sans', system-ui, sans-serif;
}
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body, #root {
  height: 100%; width: 100%; background: var(--bg-deep);
  color: var(--text-primary); font-family: var(--font-body);
  -webkit-font-smoothing: antialiased; overflow: hidden;
}
@keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
@keyframes toastIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes toastOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-20px); } }
`;

// ─── UI Components ───
const Card = ({ children, style, onClick }) => (
  <div onClick={onClick} style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", padding: "20px", ...style }}>{children}</div>
);

const Button = ({ children, variant = "primary", onClick, style, disabled }) => {
  const v = {
    primary: { background: "var(--accent-kh)", color: "#0a0a0f", fontWeight: 600 },
    secondary: { background: "var(--bg-elevated)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" },
    danger: { background: "rgba(255,138,128,0.15)", color: "var(--accent-warn)", border: "1px solid rgba(255,138,128,0.2)" },
    ghost: { background: "transparent", color: "var(--text-secondary)" },
  };
  return (
    <button disabled={disabled} onClick={onClick} style={{
      ...v[variant], padding: "12px 24px", borderRadius: "var(--radius-sm)", fontSize: "15px",
      fontFamily: "var(--font-body)", cursor: disabled ? "not-allowed" : "pointer",
      border: v[variant].border || "none", opacity: disabled ? 0.5 : 1, width: "100%", ...style,
    }}>{children}</button>
  );
};

const StarRating = ({ value, onChange, size = 32 }) => (
  <div style={{ display: "flex", gap: 6 }}>
    {[1,2,3,4,5].map(i => (
      <span key={i} onClick={() => onChange?.(i)} style={{
        fontSize: size, cursor: onChange ? "pointer" : "default",
        color: i <= value ? "var(--accent-gold)" : "var(--text-muted)",
      }}>★</span>
    ))}
  </div>
);

const TrafficLight = ({ value, onChange, labels }) => (
  <div style={{ display: "flex", gap: 10 }}>
    {[{ key: "green", color: "var(--accent-good)", label: labels?.[0] || "All good" },
      { key: "yellow", color: "var(--accent-gold)", label: labels?.[1] || "Let's talk" },
      { key: "red", color: "var(--accent-warn)", label: labels?.[2] || "Need to pause" }].map(c => (
      <button key={c.key} onClick={() => onChange?.(c.key)} style={{
        flex: 1, padding: "14px 8px", borderRadius: "var(--radius-sm)",
        border: value === c.key ? `2px solid ${c.color}` : "2px solid var(--border-subtle)",
        background: value === c.key ? `${c.color}22` : "var(--bg-elevated)",
        color: c.color, fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer",
      }}>{c.label}</button>
    ))}
  </div>
);

const TimerDisplay = ({ dur, color }) => (
  <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 16 }}>
    {[{ val: dur.days, label: "days" }, { val: dur.hours, label: "hrs" }, { val: dur.mins, label: "min" }, { val: dur.secs, label: "sec" }].map((u, i) => (
      <div key={i} style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 36, fontWeight: 700, color, lineHeight: 1 }}>
          {String(u.val).padStart(2, "0")}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>{u.label}</div>
      </div>
    ))}
  </div>
);

function Input({ label, value, onChange, placeholder, type = "text", maxLength, style: xs }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8, display: "block" }}>{label}</label>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} maxLength={maxLength}
        style={{ width: "100%", padding: "14px 16px", background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-sm)", color: "var(--text-primary)", fontSize: 16, fontFamily: "var(--font-body)", outline: "none", ...xs }} />
    </div>
  );
}

// ─── Helpers ───
function formatDuration(ms) {
  if (!ms || ms < 0) return { days: 0, hours: 0, mins: 0, secs: 0, text: "0d 0h 0m" };
  const secs = Math.floor(ms / 1000) % 60, mins = Math.floor(ms / 60000) % 60;
  const hours = Math.floor(ms / 3600000) % 24, days = Math.floor(ms / 86400000);
  return { days, hours, mins, secs, text: `${days}d ${hours}h ${mins}m` };
}
function formatDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
const LEVELS = ["Beginner", "Obedient", "Disciplined", "Devoted", "Owned Playtoy", "Perfect Sex Slave"];
function getLevelForPoints(pts) {
  if (pts >= 200) return 5; if (pts >= 120) return 4; if (pts >= 70) return 3;
  if (pts >= 35) return 2; if (pts >= 10) return 1; return 0;
}

// ═══════════════════════════════════════════
// ─── TOAST NOTIFICATION SYSTEM ────────────
// ═══════════════════════════════════════════
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((icon, title, body, color = "var(--accent-kh)") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, icon, title, body, color, exiting: false }]);
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 400);
    }, 4000);
  }, []);
  return { toasts, addToast };
}

function ToastContainer({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
      width: "calc(100% - 32px)", maxWidth: 440, zIndex: 9999, display: "flex",
      flexDirection: "column", gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: "var(--bg-elevated)", border: `1px solid ${t.color}33`,
          borderLeft: `3px solid ${t.color}`, borderRadius: "var(--radius-sm)",
          padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start",
          animation: t.exiting ? "toastOut 0.3s ease forwards" : "toastIn 0.3s ease",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>{t.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: t.color, marginBottom: 2 }}>{t.title}</p>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4 }}>{t.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════
// ─── MAIN APP ─────────────────────────────
// ═══════════════════════════════════════════
export default function App() {
  const [screen, setScreen] = useState("loading");
  const [user, setUser] = useState(null);
  const [pairData, setPairData] = useState({});
  const [now, setNow] = useState(Date.now());
  const { toasts, addToast } = useToasts();
  const lastPollRef = useRef(null);
  const reminderCheckRef = useRef(null);

  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  useEffect(() => {
    const s = document.createElement("style"); s.textContent = STYLE_TAG;
    document.head.appendChild(s); return () => s.remove();
  }, []);

  // Load saved session
  useEffect(() => {
    (async () => {
      if (api.loadToken()) {
        try {
          const u = JSON.parse(localStorage.getItem("locked_user") || "null");
          if (u) { setUser(u); const d = await api.call("status"); setPairData(d); setScreen("home"); }
          else setScreen("onboard");
        } catch { api.setToken(null); localStorage.removeItem("locked_user"); setScreen("onboard"); }
      } else setScreen("onboard");
    })();
  }, []);

  // Poll every 10s + detect new events for toasts
  useEffect(() => {
    if (!user || screen !== "home") return;
    const iv = setInterval(async () => {
      try {
        const since = lastPollRef.current || new Date(Date.now() - 15000).toISOString();
        const data = await api.call(`poll?since=${encodeURIComponent(since)}`);
        lastPollRef.current = new Date().toISOString();

        // Detect changes and show toasts
        const prev = pairData;
        if (prev.locked !== undefined && data.locked !== prev.locked) {
          if (data.locked) addToast("🔒", "Locked!", "The cage is on. Timer started.", "var(--accent-kh)");
          else addToast("🔓", "Released!", "Freedom! ...for now.", "var(--accent-good)");
        }
        if (data.new_ratings?.length) {
          const r = data.new_ratings[0];
          addToast("⭐", "New Rating!", `${"★".repeat(r.stars)}${r.note ? ` — "${r.note}"` : ""}`, "var(--accent-gold)");
        }
        if (data.new_checkins?.length) {
          const c = data.new_checkins[0];
          const icon = c.physical === "green" ? "💚" : c.physical === "yellow" ? "💛" : "❤️";
          addToast(icon, "New Check-in", `Physical: ${c.physical} · Emotional: ${c.emotional}${c.note ? ` — "${c.note}"` : ""}`,
            c.physical === "red" ? "var(--accent-warn)" : "var(--accent-good)");
        }

        setPairData(prev2 => ({ ...prev2, ...data }));
      } catch {}
    }, 10000);
    return () => clearInterval(iv);
  }, [user, screen, pairData.locked]);

  // Local daily reminders (toasts + browser notifications)
  useEffect(() => {
    if (!user || screen !== "home") return;
    const check = () => {
      const now = new Date();
      const h = now.getHours();
      const todayKey = `reminder_${now.toDateString()}`;
      const shown = JSON.parse(localStorage.getItem(todayKey) || "{}");

      // Keyholder: task idea at 9am, tonight idea at 7pm, rating at 9pm
      if (user.role === "keyholder") {
        if (h >= 9 && h < 10 && !shown.taskIdea && pairData.locked) {
          addToast("📋", "Today's task idea", "Check the Ideas tab — a new task suggestion is waiting for you.", "var(--accent-kh)");
          shown.taskIdea = true;
        }
        if (h >= 19 && h < 20 && !shown.tonightIdea && pairData.locked) {
          addToast("🌙", "Tonight's idea", "Something special for tonight is ready in the Ideas tab.", "var(--accent-kh)");
          shown.tonightIdea = true;
        }
        if (h >= 21 && h < 22 && !shown.rate) {
          addToast("⭐", "Time to rate!", "How was your sub's behavior today? Head to the Rate tab.", "var(--accent-gold)");
          shown.rate = true;
        }
      }
      // Sub: check-in at 8pm
      if (user.role === "sub") {
        if (h >= 20 && h < 21 && !shown.checkin && pairData.locked) {
          addToast("💚", "Check-in time", "How are you feeling? Your keyholder wants to know.", "var(--accent-good)");
          shown.checkin = true;
        }
      }
      localStorage.setItem(todayKey, JSON.stringify(shown));
    };
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, [user, screen, pairData.locked]);

  const handleAuth = async (userData, token) => {
    api.setToken(token);
    localStorage.setItem("locked_user", JSON.stringify(userData));
    setUser(userData);
    try { const d = await api.call("status"); setPairData(d); } catch {}
    setScreen("home");
    await requestNotificationPermission();
    await subscribeToPush();
  };

  const handleLogout = () => {
    api.setToken(null); localStorage.removeItem("locked_user"); localStorage.removeItem("locked_token");
    setUser(null); setPairData({}); setScreen("onboard");
  };

  const refreshStatus = async () => { try { const d = await api.call("status"); setPairData(d); } catch {} };

  if (screen === "loading") return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "var(--text-muted)", animation: "pulse 1.5s infinite" }}>Loading...</p>
    </div>
  );

  return (
    <>
      <ToastContainer toasts={toasts} />
      {screen === "onboard"
        ? <OnboardScreen onAuth={handleAuth} />
        : <HomeScreen user={user} pairData={pairData} now={now} onLogout={handleLogout} refreshStatus={refreshStatus} />}
    </>
  );
}

// ═══════════════════════════════════════════
// ─── ONBOARDING ───────────────────────────
// ═══════════════════════════════════════════
function OnboardScreen({ onAuth }) {
  const [mode, setMode] = useState("");
  const [step, setStep] = useState(0);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [pairCode, setPairCode] = useState("");
  const [joinOrCreate, setJoinOrCreate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const genCode = () => { const c = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; let o = ""; for (let i = 0; i < 6; i++) o += c[Math.floor(Math.random() * c.length)]; return o; };

  const handleRegister = async () => {
    setLoading(true); setError("");
    try { const d = await api.call("register", "POST", { username: username.trim(), password, role, pair_code: pairCode }); onAuth(d.user, d.token); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleLogin = async () => {
    setLoading(true); setError("");
    try { const d = await api.call("login", "POST", { username: username.trim(), password }); onAuth(d.user, d.token); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "32px 24px", maxWidth: 440, margin: "0 auto", animation: "fadeUp 0.5s ease" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🔐</div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 700, marginBottom: 8, background: "linear-gradient(135deg, var(--accent-kh), var(--accent-sub))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Locked</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>Your private couples companion</p>
      </div>

      {error && <div style={{ padding: "10px 14px", background: "rgba(255,138,128,0.1)", border: "1px solid rgba(255,138,128,0.2)", borderRadius: "var(--radius-sm)", color: "var(--accent-warn)", fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {!mode && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, animation: "fadeUp 0.3s ease" }}>
          <Button onClick={() => setMode("register")}>Create Account</Button>
          <Button variant="secondary" onClick={() => setMode("login")}>Log In</Button>
        </div>
      )}

      {mode === "login" && (
        <div style={{ animation: "fadeUp 0.3s ease" }}>
          <Input label="Username" value={username} onChange={setUsername} placeholder="Your username" />
          <Input label="Password" value={password} onChange={setPassword} placeholder="Your password" type="password" />
          <Button disabled={!username.trim() || !password || loading} onClick={handleLogin}>{loading ? "Logging in..." : "Log In"}</Button>
          <BackBtn onClick={() => { setMode(""); setError(""); }} />
        </div>
      )}

      {mode === "register" && step === 0 && (
        <div style={{ animation: "fadeUp 0.3s ease" }}>
          <Input label="Choose a username" value={username} onChange={setUsername} placeholder="Your username" />
          <Input label="Choose a password" value={password} onChange={setPassword} placeholder="Min 4 characters" type="password" />
          <Button disabled={!username.trim() || password.length < 4} onClick={() => setStep(1)}>Continue</Button>
          <BackBtn onClick={() => { setMode(""); setError(""); }} />
        </div>
      )}

      {mode === "register" && step === 1 && (
        <div style={{ animation: "fadeUp 0.3s ease" }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 16, textAlign: "center" }}>Choose your role</p>
          {[{ key: "keyholder", icon: "🗝️", label: "Keyholder", desc: "You hold the key and control the experience" },
            { key: "sub", icon: "🔒", label: "Locked", desc: "You surrender control and trust your keyholder" }].map(r => (
            <Card key={r.key} onClick={() => { setRole(r.key); setStep(2); }} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 16, marginBottom: 12, border: role === r.key ? "1px solid var(--accent-kh)" : undefined }}>
              <span style={{ fontSize: 32 }}>{r.icon}</span>
              <div><div style={{ fontWeight: 600, fontSize: 16 }}>{r.label}</div><div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>{r.desc}</div></div>
            </Card>
          ))}
          <BackBtn onClick={() => setStep(0)} />
        </div>
      )}

      {mode === "register" && step === 2 && (
        <div style={{ animation: "fadeUp 0.3s ease" }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 16, textAlign: "center" }}>Connect with your partner</p>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <Button variant={joinOrCreate === "create" ? "primary" : "secondary"} onClick={() => { setJoinOrCreate("create"); setPairCode(genCode()); }} style={{ flex: 1 }}>Create Room</Button>
            <Button variant={joinOrCreate === "join" ? "primary" : "secondary"} onClick={() => { setJoinOrCreate("join"); setPairCode(""); }} style={{ flex: 1 }}>Join Room</Button>
          </div>
          {joinOrCreate === "create" && (
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 10 }}>Share this code with your partner</p>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 36, fontWeight: 700, letterSpacing: 8, color: "var(--accent-kh)", padding: 16, background: "var(--accent-kh-dim)", borderRadius: "var(--radius-sm)" }}>{pairCode}</div>
            </div>
          )}
          {joinOrCreate === "join" && (
            <Input label="Enter pair code" value={pairCode} onChange={v => setPairCode(v.toUpperCase())} placeholder="ABC123" maxLength={6} style={{ fontSize: 24, fontFamily: "var(--font-display)", textAlign: "center", letterSpacing: 6 }} />
          )}
          {joinOrCreate && <Button disabled={pairCode.length < 4 || loading} onClick={handleRegister}>{loading ? "Creating..." : "Enter"}</Button>}
          <BackBtn onClick={() => { setStep(1); setJoinOrCreate(""); }} />
        </div>
      )}
    </div>
  );
}

function BackBtn({ onClick }) {
  return <button onClick={onClick} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", marginTop: 16, fontFamily: "var(--font-body)" }}>← Back</button>;
}

// ═══════════════════════════════════════════
// ─── HOME ─────────────────────────────────
// ═══════════════════════════════════════════
function HomeScreen({ user, pairData, now, onLogout, refreshStatus }) {
  const [tab, setTab] = useState("home");
  const isKH = user.role === "keyholder";
  const accent = isKH ? "var(--accent-kh)" : "var(--accent-sub)";
  const tabs = isKH
    ? [{ key: "home", icon: "🏠", label: "Home" }, { key: "tasks", icon: "📋", label: "Tasks" }, { key: "ideas", icon: "💡", label: "Ideas" }, { key: "rate", icon: "⭐", label: "Rate" }, { key: "stats", icon: "📊", label: "Stats" }, { key: "settings", icon: "⚙️", label: "More" }]
    : [{ key: "home", icon: "🏠", label: "Home" }, { key: "tasks", icon: "📋", label: "Tasks" }, { key: "checkin", icon: "💚", label: "Check-in" }, { key: "stats", icon: "📊", label: "Stats" }, { key: "settings", icon: "⚙️", label: "More" }];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ flex: 1, overflow: "auto", padding: "20px 16px 100px 16px" }}>
        {tab === "home" && (isKH ? <KHHome pairData={pairData} now={now} refreshStatus={refreshStatus} onOpenAchievements={() => setTab("achievements")} /> : <SubHome pairData={pairData} now={now} user={user} onOpenAchievements={() => setTab("achievements")} />)}{tab === "tasks" && <TasksScreen user={user} refreshStatus={refreshStatus} />}
        {tab === "ideas" && <IdeasScreen pairData={pairData} now={now} />}
        {tab === "rate" && <RateScreen pairData={pairData} refreshStatus={refreshStatus} />}
        {tab === "checkin" && <CheckInScreen refreshStatus={refreshStatus} />}
        {tab === "stats" && <StatsScreen />}
        {tab === "settings" && <SettingsScreen user={user} pairData={pairData} onLogout={onLogout} />}
        {tab === "achievements" && <AchievementsScreen user={user} onBack={() => setTab("home")} />}
      </div>
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, display: "flex", background: "rgba(10,10,15,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid var(--border-subtle)", padding: "8px 0 env(safe-area-inset-bottom, 12px) 0" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, background: "none", border: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "8px 4px", cursor: "pointer", opacity: tab === t.key ? 1 : 0.4 }}>
            <span style={{ fontSize: 20 }}>{t.icon}</span>
            <span style={{ fontSize: 10, color: tab === t.key ? accent : "var(--text-muted)", fontFamily: "var(--font-body)" }}>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── KH Home ───
function KHHome({ pairData, now, refreshStatus, onOpenAchievements }) {
  const elapsed = pairData.locked ? now - pairData.lock_start : 0;
  const dur = formatDuration(elapsed);
  const [showRelease, setShowRelease] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLock = async () => { setLoading(true); try { await api.call("lock", "POST"); await refreshStatus(); } catch (e) { alert(e.message); } setLoading(false); };
  const handleRelease = async (type) => { setLoading(true); try { await api.call("release", "POST", { release_type: type }); await refreshStatus(); } catch (e) { alert(e.message); } setShowRelease(false); setLoading(false); };

  const lc = pairData.latest_checkin;
  const checkinRecent = lc && (now - new Date(lc.created_at).getTime() < 86400000);

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>Welcome back</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700 }}>🗝️ Keyholder</h2>
      </div>
      <Card style={{ textAlign: "center", marginBottom: 16, background: pairData.locked ? "linear-gradient(135deg, rgba(196,160,255,0.08), rgba(128,180,255,0.05))" : "var(--bg-card)", border: pairData.locked ? "1px solid var(--border-accent)" : "1px solid var(--border-subtle)" }}>
        {pairData.locked ? (<>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 2 }}>{pairData.partner_name || "Sub"} locked for</p>
          <TimerDisplay dur={dur} color="var(--accent-kh)" />
          {!showRelease ? (
            <Button variant="danger" onClick={() => setShowRelease(true)} disabled={loading}>🔓 Release</Button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>Release type:</p>
              {["Full release", "Ruined", "Edged only", "Cleaning break"].map(t => (
                <Button key={t} variant="secondary" onClick={() => handleRelease(t)} disabled={loading}>{t}</Button>
              ))}
              <Button variant="ghost" onClick={() => setShowRelease(false)}>Cancel</Button>
            </div>
          )}
        </>) : (<>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔓</div>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 20 }}>{pairData.partner_name || "Sub"} is currently free</p>
          <Button onClick={handleLock} disabled={loading}>🔒 Lock Up</Button>
        </>)}
      </Card>
      <Card style={{ marginBottom: 16 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 4 }}>{pairData.partner_name || "Sub"}'s last check-in</p>
        {checkinRecent ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: lc.physical === "green" ? "var(--accent-good)" : lc.physical === "yellow" ? "var(--accent-gold)" : "var(--accent-warn)",
              display: "inline-block",
            }} />
            <span style={{ fontSize: 14 }}>Attention: {{green: "Perfectly teased", yellow: "Could use more", red: "Forgotten"}[lc.physical]}</span>
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: lc.emotional === "green" ? "var(--accent-good)" : lc.emotional === "yellow" ? "var(--accent-gold)" : "var(--accent-warn)",
              display: "inline-block",
              marginLeft: 4,
            }} />
            <span style={{ fontSize: 14 }}>Status: {lc.emotional === "green" ? "All good" : lc.emotional === "yellow" ? "Let's talk" : "Need to pause"}</span>
            {lc.note && <span style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>"{lc.note}"</span>}
          </div>
        ) : <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No recent check-in</p>}
      </Card>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <p style={{ color: "var(--text-secondary)", fontSize: 12 }}>Score & Rank</p>
            <p style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--accent-gold)" }}>{pairData.points || 0} pts</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-kh)" }}>{LEVELS[getLevelForPoints(pairData.points || 0)]}</p>
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>🔥 {pairData.current_streak || 0} day streak</p>
          </div>
        </div>
      </Card>
      <CustomBars isKH={true} now={now} />
      <AchievementsCard onOpen={onOpenAchievements} />
    </div>
  );
}

// ─── Sub Home ───
function SubHome({ pairData, now, user, onOpenAchievements }) {
  const elapsed = pairData.locked ? now - pairData.lock_start : 0;
  const dur = formatDuration(elapsed);
  const level = getLevelForPoints(pairData.points || 0);
  const lr = pairData.latest_rating;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>Welcome back, {user.username}</p>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700 }}>🔒 Locked</h2>
      </div>
      <Card style={{ textAlign: "center", marginBottom: 16, background: pairData.locked ? "linear-gradient(135deg, rgba(128,180,255,0.08), rgba(196,160,255,0.05))" : "var(--bg-card)", border: pairData.locked ? "1px solid rgba(128,180,255,0.15)" : "1px solid var(--border-subtle)" }}>
        {pairData.locked ? (<>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 2 }}>Locked for</p>
          <TimerDisplay dur={dur} color="var(--accent-sub)" />
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>{pairData.partner_name || "Your keyholder"} holds the key</p>
        </>) : (<>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔓</div>
          <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>You're currently free</p>
        </>)}
      </Card>
      <Card style={{ marginBottom: 16, textAlign: "center" }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 8 }}>Your Rank</p>
        <p style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--accent-kh)", marginBottom: 4 }}>{LEVELS[level]}</p>
        <p style={{ color: "var(--accent-gold)", fontSize: 18, fontWeight: 600 }}>{pairData.points || 0} points</p>
        <div style={{ width: "100%", height: 6, background: "var(--bg-elevated)", borderRadius: 3, marginTop: 12, overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 3, width: `${Math.min(100, ((pairData.points || 0) / [10, 35, 70, 120, 200, 300][level]) * 100)}%`, background: "linear-gradient(90deg, var(--accent-sub), var(--accent-kh))", transition: "width 0.5s" }} />
        </div>
        <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>🔥 {pairData.current_streak || 0} day streak</p>
      </Card>
      {lr && (
        <Card>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 8 }}>Latest rating from {pairData.partner_name || "Keyholder"}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StarRating value={lr.stars} size={24} />
            {lr.note && <span style={{ fontSize: 13, color: "var(--text-secondary)", fontStyle: "italic" }}>"{lr.note}"</span>}
          </div>
        </Card>
      )}
      <CustomBars isKH={false} now={now} />
      <AchievementsCard onOpen={onOpenAchievements} />
    </div>
  );
}

// ─── Tasks ───
function TasksScreen({ user, refreshStatus }) {
  const isKH = user.role === "keyholder";
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reward, setReward] = useState(5);
  const [penalty, setPenalty] = useState(-5);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.call("tasks?filter=active");
        setTasks(d.tasks || []);
        const h = await api.call("tasks?filter=completed");
        setHistory(h.tasks || []);
      } catch {}
    })();
  }, [refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  const handleCreate = async () => {
    setLoading(true);
    try {
      await api.call("task_create", "POST", { title, description, reward_points: reward, penalty_points: penalty });
      setTitle(""); setDescription(""); setReward(5); setPenalty(-5); setShowCreate(false); refresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  const handleComplete = async (taskId) => {
    try { await api.call("task_complete", "POST", { task_id: taskId }); refresh(); } catch (e) { alert(e.message); }
  };

  const handleReview = async (taskId, approved) => {
    try { await api.call("task_review", "POST", { task_id: taskId, approved }); await refreshStatus(); refresh(); } catch (e) { alert(e.message); }
  };

  const handleDelete = async (taskId) => {
    try { await api.call("task_delete", "POST", { task_id: taskId }); refresh(); } catch (e) { alert(e.message); }
  };

  const pendingTasks = tasks.filter(t => t.status === "pending");
  const awaitingReview = tasks.filter(t => t.status === "completed");

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>📋 Tasks</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
          {isKH ? "Assign tasks and review completions" : "Complete tasks to earn points"}
        </p>
      </div>

      {/* Keyholder: Create task button */}
      {isKH && !showCreate && (
        <Button onClick={() => setShowCreate(true)} style={{ marginBottom: 16 }}>+ New Task</Button>
      )}

      {/* Create task form */}
      {isKH && showCreate && (
        <Card style={{ marginBottom: 16, border: "1px solid var(--border-accent)" }}>
          <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 12, color: "var(--accent-kh)" }}>New Task</p>
          <Input label="Task title" value={title} onChange={setTitle} placeholder="e.g. Cook dinner tonight" />
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8, display: "block" }}>Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Any extra details..."
              rows={2} style={{ width: "100%", padding: 12, background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-sm)", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", resize: "none", outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8, display: "block" }}>
                Reward <span style={{ color: "var(--accent-good)" }}>+{reward}</span>
              </label>
              <input type="range" min="0" max="10" value={reward} onChange={e => setReward(+e.target.value)}
                style={{ width: "100%", accentColor: "var(--accent-good)" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8, display: "block" }}>
                Penalty <span style={{ color: "var(--accent-warn)" }}>{penalty}</span>
              </label>
              <input type="range" min="-10" max="0" value={penalty} onChange={e => setPenalty(+e.target.value)}
                style={{ width: "100%", accentColor: "var(--accent-warn)" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!title.trim() || loading} onClick={handleCreate} style={{ flex: 1 }}>
              {loading ? "Creating..." : "Create Task"}
            </Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)} style={{ flex: 0 }}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Awaiting review (keyholder sees approve/disapprove, sub sees "waiting") */}
      {awaitingReview.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "var(--accent-gold)", fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            ⏳ Awaiting Review ({awaitingReview.length})
          </p>
          {awaitingReview.map(t => (
            <Card key={t.id} style={{ marginBottom: 8, borderLeft: "3px solid var(--accent-gold)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>{t.title}</p>
                  {t.description && <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>{t.description}</p>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                  <span style={{ fontSize: 12, color: "var(--accent-good)" }}>+{t.reward_points}</span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}> / </span>
                  <span style={{ fontSize: 12, color: "var(--accent-warn)" }}>{t.penalty_points}</span>
                </div>
              </div>
              {isKH ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => handleReview(t.id, true)} style={{
                    flex: 1, padding: "10px", borderRadius: "var(--radius-sm)", border: "1px solid rgba(128,232,160,0.3)",
                    background: "rgba(128,232,160,0.1)", color: "var(--accent-good)", fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer",
                  }}>✓ Approve (+{t.reward_points})</button>
                  <button onClick={() => handleReview(t.id, false)} style={{
                    flex: 1, padding: "10px", borderRadius: "var(--radius-sm)", border: "1px solid rgba(255,138,128,0.3)",
                    background: "rgba(255,138,128,0.1)", color: "var(--accent-warn)", fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer",
                  }}>✗ Disapprove ({t.penalty_points})</button>
                </div>
              ) : (
                <p style={{ fontSize: 12, color: "var(--accent-gold)", fontStyle: "italic" }}>Submitted — waiting for keyholder review</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Pending tasks */}
      {pendingTasks.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            Active Tasks ({pendingTasks.length})
          </p>
          {pendingTasks.map(t => (
            <Card key={t.id} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>{t.title}</p>
                  {t.description && <p style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>{t.description}</p>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                  <span style={{ fontSize: 12, color: "var(--accent-good)" }}>+{t.reward_points}</span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}> / </span>
                  <span style={{ fontSize: 12, color: "var(--accent-warn)" }}>{t.penalty_points}</span>
                </div>
              </div>
              {isKH ? (
                <button onClick={() => handleDelete(t.id)} style={{
                  background: "none", border: "none", color: "var(--text-muted)", fontSize: 12, cursor: "pointer", fontFamily: "var(--font-body)",
                }}>Delete task</button>
              ) : (
                <Button onClick={() => handleComplete(t.id)} style={{ background: "var(--accent-good)", color: "#0a0a0f", padding: "10px" }}>
                  ✓ Mark Complete
                </Button>
              )}
            </Card>
          ))}
        </div>
      ) : (
        !showCreate && awaitingReview.length === 0 && (
          <Card style={{ textAlign: "center", padding: 32, marginBottom: 16 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
              {isKH ? "No active tasks. Create one above!" : "No tasks assigned yet"}
            </p>
          </Card>
        )
      )}

      {/* History toggle */}
      {history.length > 0 && (
        <div>
          <button onClick={() => setShowHistory(!showHistory)} style={{
            background: "none", border: "none", color: "var(--text-secondary)", fontSize: 13,
            cursor: "pointer", fontFamily: "var(--font-body)", marginBottom: 10,
          }}>{showHistory ? "▾ Hide history" : "▸ Show history"} ({history.length})</button>

          {showHistory && history.map(t => (
            <div key={t.id} style={{
              padding: "10px 0", borderBottom: "1px solid var(--border-subtle)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              opacity: 0.7,
            }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, textDecoration: "line-through", color: "var(--text-muted)" }}>{t.title}</p>
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: t.status === "approved" ? "var(--accent-good)" : "var(--accent-warn)",
                }}>{t.status === "approved" ? `✓ Approved (+${t.reward_points})` : `✗ Disapproved (${t.penalty_points})`}</span>
              </div>
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{formatDate(t.reviewed_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ideas ───
function IdeasScreen({ pairData, now }) {
  const [category, setCategory] = useState("tasks");
  const [swipeItems, setSwipeItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dailyPick, setDailyPick] = useState(null);
  const [pickHistory, setPickHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [loading, setLoading] = useState(false);

  const elapsed = pairData.locked ? now - pairData.lock_start : 0;
  const days = Math.floor(elapsed / 86400000);
  const phase = days <= 3 ? "early" : days <= 10 ? "mid" : "late";

  const cats = [
    { key: "tasks", label: "📋 Tasks", daily: true },
    { key: "tonight", label: "🌙 Tonight", daily: true },
    { key: "tease", label: "🔥 Tease", daily: false },
    { key: "texts", label: "💬 Texts", daily: false },
    { key: "games", label: "🎲 Games", daily: false },
  ];

  const isDaily = category === "tasks" || category === "tonight";

  // Load content when category changes
  useEffect(() => {
    (async () => {
      setLoading(true);
      setDailyPick(null);
      setSwipeItems([]);
      setPickHistory([]);
      setShowHistory(false);
      setShowFavorites(false);
      setIdx(0);
      try {
        if (isDaily) {
          const d = await api.call(`daily_pick?category=${category}`);
          setDailyPick(d.pick);
          const h = await api.call(`pick_history?category=${category}&limit=14`);
          setPickHistory(h.picks || []);
        } else {
          const phaseParam = category === "tease" ? `&phase=${phase}` : "";
          const d = await api.call(`suggestions?category=${category}${phaseParam}`);
          setSwipeItems(d.suggestions || []);
        }
        // Always load favorites
        const f = await api.call("favorites");
        setFavorites(f.favorites || []);
      } catch {}
      setLoading(false);
    })();
  }, [category]);

  const toggleFav = async (suggestionId) => {
    try {
      const res = await api.call("fav_toggle", "POST", { suggestion_id: suggestionId });
      // Update swipeItems inline
      setSwipeItems(prev => prev.map(s =>
        s.id === suggestionId ? { ...s, favorited: res.favorited } : s
      ));
      // Refresh favorites list
      const f = await api.call("favorites");
      setFavorites(f.favorites || []);
    } catch {}
  };

  const currentItem = swipeItems[idx];

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>💡 Ideas</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
          {pairData.locked ? `Day ${days + 1} of current session` : "No active session"}
        </p>
      </div>

      {/* Category pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
        {cats.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} style={{
            padding: "8px 14px", borderRadius: 20, border: "none",
            background: category === c.key ? "var(--accent-kh-dim)" : "var(--bg-elevated)",
            color: category === c.key ? "var(--accent-kh)" : "var(--text-secondary)",
            fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer", whiteSpace: "nowrap",
            fontWeight: category === c.key ? 600 : 400,
          }}>{c.label}</button>
        ))}
      </div>

      {loading && <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 32 }}>Loading...</p>}

      {/* ── Daily pick view (tasks / tonight) ── */}
      {!loading && isDaily && (
        <>
          {dailyPick ? (
            <Card style={{
              minHeight: 140, marginBottom: 16,
              background: category === "tonight"
                ? "linear-gradient(135deg, rgba(128,100,220,0.1), rgba(80,60,160,0.06))"
                : "linear-gradient(135deg, rgba(196,160,255,0.06), rgba(255,212,128,0.04))",
              border: "1px solid var(--border-accent)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: "var(--accent-kh)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                  {category === "tonight" ? "🌙 Tonight's idea" : "📋 Today's task suggestion"}
                </span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{dailyPick.date}</span>
              </div>
              {dailyPick.intensity > 1 && (
                <div style={{ marginBottom: 8, display: "flex", gap: 3 }}>
                  {Array.from({ length: dailyPick.intensity }).map((_, i) => (
                    <span key={i} style={{ fontSize: 10, color: "var(--accent-warn)" }}>🔥</span>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 17, lineHeight: 1.6, fontFamily: "var(--font-display)", fontStyle: "italic" }}>
                {dailyPick.text}
              </p>
            </Card>
          ) : (
            <Card style={{ textAlign: "center", padding: 32, marginBottom: 16 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
                {category === "tonight" ? "No tonight suggestion yet — check back at 7 PM" : "No task suggestion for today"}
              </p>
            </Card>
          )}

          {/* Favorites section for daily views */}
          {favorites.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <button onClick={() => setShowFavorites(!showFavorites)} style={{
                background: "none", border: "none", color: "var(--accent-warn)", fontSize: 13,
                cursor: "pointer", fontFamily: "var(--font-body)", marginBottom: 10, fontWeight: 600,
              }}>
                {showFavorites ? "▾" : "▸"} ❤️ Favorites ({favorites.length})
              </button>
              {showFavorites && favorites.map((f, i) => (
                <div key={f.id} style={{
                  padding: "10px 12px", marginBottom: 6, borderRadius: "var(--radius-sm)",
                  background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <p style={{ fontSize: 14, color: "var(--text-primary)", lineHeight: 1.5, flex: 1 }}>{f.text}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 8 }}>
                      <span style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase" }}>{f.category}</span>
                      <button onClick={() => toggleFav(f.id)} style={{
                        background: "none", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0,
                      }}>❤️</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Past picks history */}
          {pickHistory.length > 1 && (
            <div>
              <button onClick={() => setShowHistory(!showHistory)} style={{
                background: "none", border: "none", color: "var(--text-secondary)", fontSize: 13,
                cursor: "pointer", fontFamily: "var(--font-body)", marginBottom: 10,
              }}>{showHistory ? "▾ Hide past suggestions" : "▸ Show past suggestions"} ({pickHistory.length - 1})</button>

              {showHistory && pickHistory.slice(1).map((p, i) => (
                <div key={i} style={{ padding: "12px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.pick_date}</span>
                    {p.intensity > 1 && (
                      <span style={{ fontSize: 10 }}>{Array.from({ length: p.intensity }).map(() => "🔥").join("")}</span>
                    )}
                  </div>
                  <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5 }}>{p.text}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Swipeable view (tease, texts, games) ── */}
      {!loading && !isDaily && (
        <>
          {swipeItems.length > 0 ? (
            <>
              <Card style={{
                minHeight: 160, display: "flex", flexDirection: "column", justifyContent: "space-between",
                background: "linear-gradient(135deg, rgba(196,160,255,0.06), rgba(255,212,128,0.04))",
                border: "1px solid var(--border-accent)", marginBottom: 16,
              }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      {currentItem?.intensity > 1 && (
                        <div style={{ marginBottom: 10, display: "flex", gap: 3 }}>
                          {Array.from({ length: currentItem.intensity }).map((_, i) => (
                            <span key={i} style={{ fontSize: 10, color: "var(--accent-warn)" }}>🔥</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={() => toggleFav(currentItem.id)} style={{
                      background: "none", border: "none", cursor: "pointer", fontSize: 22, lineHeight: 1,
                      padding: "0 0 0 8px", transition: "transform 0.2s",
                    }}>{currentItem?.favorited ? "❤️" : "🤍"}</button>
                  </div>
                  <p style={{ fontSize: 17, lineHeight: 1.6, fontFamily: "var(--font-display)", fontStyle: "italic" }}>
                    {currentItem?.text}
                  </p>
                </div>
                <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12 }}>
                  {idx + 1} of {swipeItems.length}
                </p>
              </Card>
              <Button variant="secondary" onClick={() => setIdx(p => (p + 1) % swipeItems.length)}>
                Next idea →
              </Button>

              {/* Favorites section for swipeable views */}
              {favorites.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <button onClick={() => setShowFavorites(!showFavorites)} style={{
                    background: "none", border: "none", color: "var(--accent-warn)", fontSize: 13,
                    cursor: "pointer", fontFamily: "var(--font-body)", marginBottom: 10, fontWeight: 600,
                  }}>
                    {showFavorites ? "▾" : "▸"} ❤️ Favorites ({favorites.length})
                  </button>
                  {showFavorites && favorites.map((f) => (
                    <div key={f.id} style={{
                      padding: "10px 12px", marginBottom: 6, borderRadius: "var(--radius-sm)",
                      background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <p style={{ fontSize: 14, color: "var(--text-primary)", lineHeight: 1.5, flex: 1 }}>{f.text}</p>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 8 }}>
                          <span style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase" }}>{f.category}</span>
                          <button onClick={() => toggleFav(f.id)} style={{
                            background: "none", border: "none", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0,
                          }}>❤️</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Card style={{ textAlign: "center", padding: 32 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 14 }}>No suggestions available</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Rate ───
function RateScreen({ pairData, refreshStatus }) {
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState([]);

  useEffect(() => { (async () => { try { const d = await api.call("ratings?limit=7"); setRatings(d.ratings || []); } catch {} })(); }, [saved]);

  const handleRate = async () => {
    setLoading(true);
    try { await api.call("rate", "POST", { stars, note }); await refreshStatus(); setSaved(true); setTimeout(() => { setSaved(false); setStars(0); setNote(""); }, 2000); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>⭐ Rate {pairData.partner_name || "Sub"}</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>How was their behavior today?</p>
      </div>
      
      <Card style={{ marginBottom: 12 }}>
        {saved ? (
          <div style={{ textAlign: "center", padding: 20 }}><div style={{ fontSize: 40, marginBottom: 8 }}>✨</div><p style={{ color: "var(--accent-good)", fontWeight: 600 }}>Rating saved!</p></div>
        ) : (<>
          <div style={{ textAlign: "center", marginBottom: 16 }}><StarRating value={stars} onChange={setStars} size={40} /></div>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note..." rows={3} style={{ width: "100%", padding: 12, background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", resize: "none", outline: "none", marginBottom: 12 }} />
          <Button disabled={stars === 0 || loading} onClick={handleRate}>{loading ? "Saving..." : "Submit Rating"}</Button>
        </>)}
      </Card>
      <Card style={{ marginBottom: 16 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Points per rating</p>
        {[
          { stars: 1, pts: -5, color: "var(--accent-warn)" },
          { stars: 2, pts: -2, color: "var(--accent-warn)" },
          { stars: 3, pts: +1, color: "var(--text-secondary)" },
          { stars: 4, pts: +3, color: "var(--accent-good)" },
          { stars: 5, pts: +5, color: "var(--accent-good)" },
        ].map(r => (
          <div key={r.stars} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
            <span style={{ fontSize: 14 }}>{"★".repeat(r.stars)}{"☆".repeat(5 - r.stars)}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: r.color }}>{r.pts > 0 ? "+" : ""}{r.pts} pts</span>
          </div>
        ))}
        <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>3+ day streak: +1 bonus · 7+ day streak: +3 bonus</p>
      </Card>
      {ratings.length > 0 && <div>
        <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 10 }}>Recent ratings</p>
        {ratings.map((r, i) => (
          <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><StarRating value={r.stars} size={16} />{r.note && <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{r.note}</p>}</div>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{formatDate(r.created_at)}</span>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ─── Check-in ───
function CheckInScreen({ refreshStatus }) {
  const [physical, setPhysical] = useState("");
  const [emotional, setEmotional] = useState("");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkins, setCheckins] = useState([]);

  useEffect(() => { (async () => { try { const d = await api.call("checkins?limit=5"); setCheckins(d.checkins || []); } catch {} })(); }, [saved]);

  const handleSubmit = async () => {
    setLoading(true);
    try { await api.call("checkin", "POST", { physical, emotional, note }); await refreshStatus(); setSaved(true); setTimeout(() => { setSaved(false); setPhysical(""); setEmotional(""); setNote(""); }, 2000); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>💚 Check-in</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>How are you feeling?</p>
      </div>
      <Card style={{ marginBottom: 16 }}>
        {saved ? (
          <div style={{ textAlign: "center", padding: 20 }}><div style={{ fontSize: 40, marginBottom: 8 }}>💚</div><p style={{ color: "var(--accent-good)", fontWeight: 600 }}>Check-in submitted!</p></div>
        ) : (<>
          <div style={{ marginBottom: 16 }}><p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8 }}>Enough attention?</p><TrafficLight value={physical} onChange={setPhysical} labels={["Perfectly teased", "Could use more", "Forgotten"]} /></div>
          <div style={{ marginBottom: 16 }}><p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8 }}>Everything okay?</p><TrafficLight value={emotional} onChange={setEmotional} /></div>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Anything you want to share... (optional)" rows={3} style={{ width: "100%", padding: 12, background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", resize: "none", outline: "none", marginBottom: 12 }} />
          <Button disabled={!physical || !emotional || loading} onClick={handleSubmit} style={{ background: "var(--accent-good)", color: "#0a0a0f" }}>{loading ? "Submitting..." : "Submit Check-in"}</Button>
        </>)}
      </Card>
      {checkins.length > 0 && <div>
        <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 10 }}>Recent check-ins</p>
        {checkins.map((c, i) => (
          <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.physical === "green" ? "var(--accent-good)" : c.physical === "yellow" ? "var(--accent-gold)" : "var(--accent-warn)" }} />
              <span style={{ fontSize: 13 }}>Attention: {{green: "Perfectly teased", yellow: "Could use more", red: "Forgotten"}[c.physical]} · Status: {c.emotional === "green" ? "All good" : c.emotional === "yellow" ? "Let's talk" : "Need to pause"}</span>
            </div>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{formatDate(c.created_at)}</span>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ─── Stats ───
function StatsScreen() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calMonth, setCalMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [tooltip, setTooltip] = useState(null);
 
  useEffect(() => { (async () => { try { setStats(await api.call("stats")); } catch {} setLoading(false); })(); }, []);
 
  if (loading) return <p style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>Loading stats...</p>;
  if (!stats) return <p style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>No data yet</p>;
  const tr = stats.time_records;
 
  // Release type colors
  const releaseColors = {
    "Full release": "var(--accent-good)",
    "Ruined": "var(--accent-gold)",
    "Edged only": "var(--accent-sub)",
    "Cleaning break": "var(--text-muted)",
  };
 
  // Calendar logic
  const calYear = calMonth.year;
  const calMo = calMonth.month;
  const firstDay = new Date(calYear, calMo, 1).getDay();
  const daysInMonth = new Date(calYear, calMo + 1, 0).getDate();
  const monthName = new Date(calYear, calMo).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const today = new Date().toISOString().split("T")[0];
 
  const prevMonth = () => setCalMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 });
  const nextMonth = () => {
    const now = new Date();
    if (calYear === now.getFullYear() && calMo === now.getMonth()) return;
    setCalMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 });
  };
 
  const lockedSet = new Set(stats.calendar?.locked_days || []);
  const releasesMap = stats.calendar?.releases || {};
 
  const getDayColor = (dateStr) => {
    if (releasesMap[dateStr]) {
      const types = releasesMap[dateStr];
      const best = ["Full release", "Ruined", "Edged only", "Cleaning break"].find(t => types.includes(t));
      return releaseColors[best] || "var(--accent-kh)";
    }
    if (lockedSet.has(dateStr)) return "var(--accent-kh)";
    return null;
  };
 
  const getDayLabel = (dateStr) => {
    if (releasesMap[dateStr]) {
      const types = releasesMap[dateStr];
      return types.join(", ");
    }
    if (lockedSet.has(dateStr)) return "Denied";
    return "Not locked";
  };
 
  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 20 }}>📊 Statistics</h2>
 
      {/* Ranks & Points */}
      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Ranks & Points</p>
        {[
          { name: "Beginner", min: 0, max: 9 },
          { name: "Obedient", min: 10, max: 34 },
          { name: "Disciplined", min: 35, max: 69 },
          { name: "Devoted", min: 70, max: 119 },
          { name: "Owned Playtoy", min: 120, max: 199 },
          { name: "Perfect Sex Slave", min: 200, max: null },
        ].map((r, i) => {
          const current = getLevelForPoints(stats.points) === i;
          return (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 10px", marginBottom: 4, borderRadius: "var(--radius-sm)",
              background: current ? "var(--accent-kh-dim)" : "transparent",
              border: current ? "1px solid var(--border-accent)" : "1px solid transparent",
            }}>
              <span style={{ fontSize: 14, fontWeight: current ? 700 : 400, color: current ? "var(--accent-kh)" : "var(--text-secondary)" }}>
                {current ? "▸ " : ""}{r.name}
              </span>
              <span style={{ fontSize: 12, color: current ? "var(--accent-kh)" : "var(--text-muted)" }}>
                {r.max ? `${r.min} – ${r.max} pts` : `${r.min}+ pts`}
              </span>
            </div>
          );
        })}
      </Card>
 
      {/* Time since last release by type */}
      {stats.last_release && (
        <Card style={{ marginBottom: 12 }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Time Since Last</p>
          {["Full release", "Ruined", "Edged only", "Cleaning break"].map(type => {
            const data = stats.last_release[type];
            return (
              <div key={type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: releaseColors[type], display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{type}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--font-display)", color: data?.time_since ? "var(--text-primary)" : "var(--text-muted)" }}>
                  {data?.time_since ? formatDuration(data.time_since).text : "Never"}
                </span>
              </div>
            );
          })}
        </Card>
      )}
 
      {/* Longest denial streak by type */}
      {stats.longest_denial && (
        <Card style={{ marginBottom: 12 }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Longest Denial Streak</p>
          {["Full release", "Ruined", "Edged only", "Cleaning break"].map(type => {
            const ms = stats.longest_denial[type] || 0;
            return (
              <div key={type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: releaseColors[type], display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Without {type.toLowerCase()}</span>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, fontFamily: "var(--font-display)", color: ms > 0 ? "var(--accent-kh)" : "var(--text-muted)" }}>
                  {ms > 0 ? formatDuration(ms).text : "—"}
                </span>
              </div>
            );
          })}
        </Card>
      )}
 
      {/* Calendar */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <button onClick={prevMonth} style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 18, cursor: "pointer", padding: "4px 8px" }}>◀</button>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{monthName}</p>
          <button onClick={nextMonth} style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 18, cursor: "pointer", padding: "4px 8px",
            opacity: calYear === new Date().getFullYear() && calMo === new Date().getMonth() ? 0.3 : 1 }}>▶</button>
        </div>
        {/* Day labels */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 10, color: "var(--text-muted)", padding: 4 }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${String(calMo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const color = getDayColor(dateStr);
            const isToday = dateStr === today;
            const hasRelease = releasesMap[dateStr];
            const isLocked = lockedSet.has(dateStr);
            const isFuture = dateStr > today;
 
            return (
              <div key={day} onClick={() => { if (!isFuture && (isLocked || hasRelease)) setTooltip(prev => prev === dateStr ? null : dateStr); }}
                style={{
                  textAlign: "center", padding: "6px 2px", borderRadius: 6, cursor: (isLocked || hasRelease) && !isFuture ? "pointer" : "default",
                  background: color ? `${color}22` : "transparent",
                  border: isToday ? "1px solid var(--accent-kh)" : "1px solid transparent",
                  position: "relative",
                }}>
                <span style={{ fontSize: 12, color: isFuture ? "var(--text-muted)" : color || "var(--text-muted)", fontWeight: isToday ? 700 : 400 }}>{day}</span>
                {hasRelease && <div style={{ width: 4, height: 4, borderRadius: "50%", background: getDayColor(dateStr), margin: "2px auto 0" }} />}
                {tooltip === dateStr && (
                  <div style={{
                    position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)",
                    background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)", borderRadius: 8,
                    padding: "6px 10px", fontSize: 11, color: "var(--text-primary)", whiteSpace: "nowrap",
                    zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  }}>{getDayLabel(dateStr)}</div>
                )}
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12, justifyContent: "center" }}>
          {[
            { label: "Denied", color: "var(--accent-kh)" },
            { label: "Full", color: "var(--accent-good)" },
            { label: "Ruined", color: "var(--accent-gold)" },
            { label: "Edged", color: "var(--accent-sub)" },
            { label: "Cleaning", color: "var(--text-muted)" },
          ].map(l => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: l.color, display: "inline-block" }} />
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{l.label}</span>
            </div>
          ))}
        </div>
      </Card>
 
      {/* Time Records */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>Total Time Locked</p>
          <p style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--accent-good)" }}>{formatDuration(tr.total).text}</p>
        </div>
      </Card>
 
      {/* Summary */}
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div><p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Sessions</p><p style={{ fontSize: 28, fontWeight: 700, fontFamily: "var(--font-display)" }}>{stats.session_count}</p></div>
          <div><p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Avg rating</p><p style={{ fontSize: 28, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--accent-gold)" }}>{stats.rating_stats.average || "—"} ★</p></div>
        </div>
      </Card>
 
      {/* Release types */}
      {Object.keys(stats.release_types || {}).length > 0 && (
        <Card>{Object.entries(stats.release_types).map(([type, count]) => (
          <div key={type} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border-subtle)" }}>
            <span style={{ fontSize: 14 }}>{type}</span><span style={{ color: "var(--accent-kh)", fontWeight: 600 }}>{count}</span>
          </div>
        ))}</Card>
      )}
    </div>
  );
}

// ─── Custom progress bars (keyholder-defined) ───
function CustomBars({ isKH, now }) {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  // create form state
  const [type, setType] = useState("counter");
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState(10);
  const [amount, setAmount] = useState(30);
  const [unit, setUnit] = useState("days");

  const load = async () => {
    try { const d = await api.call("bars"); setBars(d.bars || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const adjust = async (barId, delta) => {
    setBars(prev => prev.map(b => b.id === barId
      ? { ...b, current_value: Math.max(0, Math.min(b.target_value, b.current_value + delta)) }
      : b));
    try { await api.call("bar_adjust", "POST", { bar_id: barId, amount: delta }); } catch { load(); }
  };

  const del = async (barId) => {
    setBars(prev => prev.filter(b => b.id !== barId));
    try { await api.call("bar_delete", "POST", { bar_id: barId }); } catch { load(); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const body = type === "counter"
        ? { type, title, target_value: +target }
        : { type, title, amount: +amount, unit };
      await api.call("bar_create", "POST", body);
      setTitle(""); setTarget(10); setAmount(30); setUnit("days"); setType("counter");
      setShowCreate(false);
      await load();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  // fill % + label per bar
  const barView = (b) => {
    if (b.type === "counter") {
      const pct = b.target_value > 0 ? Math.min(100, (b.current_value / b.target_value) * 100) : 0;
      return { pct, label: `${b.current_value} / ${b.target_value}` };
    }
    const span = b.end_ts - b.start_ts;
    const done = now - b.start_ts;
    const pct = span > 0 ? Math.max(0, Math.min(100, (done / span) * 100)) : 100;
    const remaining = b.end_ts - now;
    let label;
    if (remaining <= 0) label = "Complete";
    else if (remaining >= 86400000) label = `${Math.ceil(remaining / 86400000)} days left`;
    else if (remaining >= 3600000) label = `${Math.ceil(remaining / 3600000)} hrs left`;
    else label = `${Math.ceil(remaining / 60000)} min left`;
    return { pct, label };
  };

  if (loading) return null;
  if (!isKH && bars.length === 0) return null; // sub sees nothing if none exist

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Goals</p>
        {isKH && bars.length < 5 && !showCreate && (
          <button onClick={() => setShowCreate(true)} style={{ background: "none", border: "none", color: "var(--accent-kh)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: 600 }}>+ Add</button>
        )}
      </div>

      {bars.map(b => {
        const { pct, label } = barView(b);
        return (
          <Card key={b.id} style={{ marginBottom: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{b.title}</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isKH && b.type === "counter" && (
                <button onClick={() => adjust(b.id, -1)} style={barBtnStyle}>−</button>
              )}
              <div style={{ flex: 1, height: 8, background: "var(--bg-elevated)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: "linear-gradient(90deg, var(--accent-sub), var(--accent-kh))", transition: "width 0.4s" }} />
              </div>
              {isKH && b.type === "counter" && (
                <button onClick={() => adjust(b.id, 1)} style={barBtnStyle}>+</button>
              )}
            </div>
            {isKH && (
              <button onClick={() => del(b.id)} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 11, cursor: "pointer", fontFamily: "var(--font-body)", marginTop: 8 }}>Delete</button>
            )}
          </Card>
        );
      })}

      {isKH && showCreate && (
        <Card style={{ marginBottom: 8, border: "1px solid var(--border-accent)" }}>
          <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: "var(--accent-kh)" }}>New goal</p>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <Button variant={type === "counter" ? "primary" : "secondary"} onClick={() => setType("counter")} style={{ flex: 1, padding: "10px" }}>Counter</Button>
            <Button variant={type === "time" ? "primary" : "secondary"} onClick={() => setType("time")} style={{ flex: 1, padding: "10px" }}>Time</Button>
          </div>
          <Input label="Title" value={title} onChange={setTitle} placeholder={type === "time" ? "e.g. Locktober" : "e.g. Good behavior"} maxLength={100} />
          {type === "counter" ? (
            <Input label="Target number" value={target} onChange={setTarget} type="number" placeholder="100" />
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Input label="Duration" value={amount} onChange={setAmount} type="number" placeholder="30" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8, display: "block" }}>Unit</label>
                <select value={unit} onChange={e => setUnit(e.target.value)} style={{ width: "100%", padding: "14px 16px", background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", color: "var(--text-primary)", fontSize: 16, fontFamily: "var(--font-body)", outline: "none" }}>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Button disabled={!title.trim() || busy || (type === "counter" ? +target < 1 : +amount < 1)} onClick={create} style={{ flex: 1 }}>{busy ? "Creating..." : "Create"}</Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)} style={{ flex: 0 }}>Cancel</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

const barBtnStyle = {
  width: 32, height: 32, flexShrink: 0, borderRadius: 8,
  background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
  color: "var(--text-primary)", fontSize: 18, lineHeight: 1, cursor: "pointer",
  fontFamily: "var(--font-body)", display: "flex", alignItems: "center", justifyContent: "center",
};

// ─── Achievements card (shown on Home) ───
function AchievementsCard({ onOpen }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    (async () => {
      try { const d = await api.call("achievements"); setCount({ u: d.unlocked, t: d.total }); } catch {}
    })();
  }, []);
  return (
    <Card onClick={onOpen} style={{ cursor: "pointer", marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 24 }}>🏆</span>
        <div>
          <p style={{ fontWeight: 600, fontSize: 15 }}>Achievements</p>
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>{count ? `${count.u} of ${count.t} unlocked` : "..."}</p>
        </div>
      </div>
      <span style={{ fontSize: 18, color: "var(--text-muted)" }}>›</span>
    </Card>
  );
}

// ─── Achievements screen (list + detail) ───
function AchievementsScreen({ user, onBack }) {
  const isKH = user.role === "keyholder";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const d = await api.call("achievements"); setItems(d.achievements || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (ach) => {
    setBusy(true);
    try {
      await api.call(ach.unlocked ? "achievement_revoke" : "achievement_grant", "POST", { achievement_id: ach.id });
      setItems(prev => prev.map(a => a.id === ach.id ? { ...a, unlocked: !a.unlocked } : a));
      setSelected(prev => prev ? { ...prev, unlocked: !prev.unlocked } : prev);
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  // ── Detail view ──
  if (selected) {
    return (
      <div style={{ animation: "fadeUp 0.4s ease" }}>
        <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", marginBottom: 16, fontFamily: "var(--font-body)" }}>← All achievements</button>
        <Card style={{
          textAlign: "center", padding: 28, marginBottom: 16,
          background: selected.unlocked ? "linear-gradient(135deg, rgba(255,212,128,0.1), rgba(196,160,255,0.05))" : "var(--bg-card)",
          border: selected.unlocked ? "1px solid rgba(255,212,128,0.25)" : "1px solid var(--border-subtle)",
        }}>
          <div style={{ fontSize: 44, marginBottom: 12, opacity: selected.unlocked ? 1 : 0.35 }}>{selected.unlocked ? "🏆" : "🔒"}</div>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, marginBottom: 8, color: selected.unlocked ? "var(--accent-gold)" : "var(--text-primary)" }}>{selected.title}</h3>
          <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.6 }}>{selected.description}</p>
          {selected.unlocked && selected.unlocked_at && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>Unlocked {formatDate(selected.unlocked_at)}</p>
          )}
        </Card>
        {isKH ? (
          selected.unlocked
            ? <Button variant="danger" disabled={busy} onClick={() => toggle(selected)}>{busy ? "..." : "Lock again"}</Button>
            : <Button disabled={busy} onClick={() => toggle(selected)} style={{ background: "var(--accent-gold)", color: "#0a0a0f" }}>{busy ? "..." : "🏆 Mark as achieved"}</Button>
        ) : (
          <p style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>
            {selected.unlocked ? "You've earned this one 🏆" : "Not yet unlocked"}
          </p>
        )}
      </div>
    );
  }

  // ── List view (groups by category string, in the order the data arrives) ──
  const unlockedCount = items.filter(a => a.unlocked).length;
  const seen = new Set();
  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", marginBottom: 12, fontFamily: "var(--font-body)" }}>← Home</button>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>🏆 Achievements</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>{unlockedCount} of {items.length} unlocked</p>
      </div>
      {loading ? <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 32 }}>Loading...</p> : (
        items.map((a, i) => {
          const newCategory = !seen.has(a.category);
          if (newCategory) seen.add(a.category);
          return (
            <div key={a.id}>
              {newCategory && (
                <p style={{ color: "var(--text-secondary)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, margin: i === 0 ? "0 0 8px" : "20px 0 8px" }}>{a.category}</p>
              )}
              <Card onClick={() => setSelected(a)} style={{
                padding: "14px 16px", marginBottom: 6, cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                opacity: a.unlocked ? 1 : 0.6,
              }}>
                <span style={{ fontSize: 15, fontWeight: a.unlocked ? 600 : 400, color: a.unlocked ? "var(--text-primary)" : "var(--text-secondary)" }}>{a.title}</span>
                <span style={{ fontSize: 18 }}>{a.unlocked ? "🏆" : "🔒"}</span>
              </Card>
            </div>
          );
        })
      )}
    </div>
  );
}
// ─── Settings ───
function SettingsScreen({ user, pairData, onLogout }) {
  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, marginBottom: 20 }}>⚙️ Settings</h2>
      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 8 }}>Profile</p>
        <p style={{ fontWeight: 600, fontSize: 16 }}>{user.username}</p>
        <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>{user.role === "keyholder" ? "🗝️ Keyholder" : "🔒 Locked"}</p>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 8 }}>Connection</p>
        <p style={{ fontSize: 14 }}>Room: <strong style={{ color: "var(--accent-kh)", letterSpacing: 2 }}>{user.pair_code}</strong></p>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>Partner: {pairData.partner_name || "Waiting..."}</p>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>📱 Install as App</p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>
          Add Locked to your home screen for a full app experience and to receive notifications.
        </p>
        <details style={{ marginBottom: 8 }}>
          <summary style={{ fontSize: 13, color: "var(--accent-kh)", cursor: "pointer", padding: "6px 0", fontWeight: 600 }}>iPhone / iPad</summary>
          <ol style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, paddingLeft: 20, marginTop: 8 }}>
            <li>Open this site in <strong>Safari</strong> (not Chrome)</li>
            <li>Tap the <strong>Share</strong> button (square with arrow ↑)</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>Add</strong> in the top right</li>
            <li>Open the app from your home screen and allow notifications when prompted</li>
          </ol>
        </details>
        <details>
          <summary style={{ fontSize: 13, color: "var(--accent-kh)", cursor: "pointer", padding: "6px 0", fontWeight: 600 }}>Android</summary>
          <ol style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, paddingLeft: 20, marginTop: 8 }}>
            <li>Open this site in <strong>Chrome</strong></li>
            <li>Tap the <strong>three dots menu</strong> (top right)</li>
            <li>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></li>
            <li>Confirm by tapping <strong>Install</strong></li>
            <li>Open the app and allow notifications when prompted</li>
          </ol>
        </details>
      </Card>
      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>Support us!</p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 12 }}>
          True Love is free and private companion app for couples exploring chastity dynamics. If you'd like to support development, consider a crypto donation:
        </p>
        {[
          { name: "Bitcoin (BTC)", address: "bc1q2wh2x2tjz7ugzp2hw90glclg73p4jkeakfeqfa" },
          { name: "Ethereum (ETH)", address: "0x5B83076b24646F9E67C27c876C8d715d9C4624E2" },
          { name: "Solana (SOL)", address: "4y4EkC4BmAtuuvDJ2iDFjWMSKioRjFp5FepS4Pmwqfdc" },
        ].map(c => (
          <div key={c.name} style={{ marginBottom: 10 }}>
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>{c.name}</p>
            <div onClick={() => { navigator.clipboard?.writeText(c.address); }} style={{
              padding: "10px 12px", background: "var(--bg-elevated)", border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-sm)", fontSize: 11, fontFamily: "monospace", color: "var(--accent-kh)",
              wordBreak: "break-all", cursor: "pointer",
            }}>{c.address}</div>
          </div>
        ))}
        <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8, textAlign: "center" }}>Tap an address to copy</p>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Feedback</p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
          Found a bug or have a suggestion? Email <a href="mailto:denial@trueloveislocked.com" style={{ color: "var(--accent-kh)" }}>denial@trueloveislocked.com</a>
        </p>
      </Card>

      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 11, marginTop: 16, marginBottom: 8 }}>
        True Love v1.04 · Made with 🔒
      </p>
      <Button variant="danger" onClick={onLogout} style={{ marginTop: 8 }}>Log Out</Button>
    </div>
  );
}